import * as React from 'react';
import propz from 'propz';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { ClubProgrammeBookingClub, ClubProgrammeBookingItem } from 'Src/helpers/service/admin/clubProgrammes';
import {
  fmtAcceptedAt,
  isConfirmedClubItem,
  isConfirmedWaitingItem,
  isSelectedClubItem,
  isSelectedWaitingItem,
  sortBookingItemsForLists
} from '../helpers/clubProgrammeBookingsBoard';

type Props = {
  isOpen: boolean;
  clubId: string;
  clubName: string;
  club: ClubProgrammeBookingClub | null;
  actionKey: string | null;
  onClose: () => void;
  onConfirmClub: (clubId: string) => void;
};

export const ClubProgrammeClubListsModal: React.FC<Props> = ({
  isOpen,
  clubId,
  clubName,
  club,
  actionKey,
  onClose,
  onConfirmClub
}) => {
  if (!isOpen) return null;

  const clubItems: ClubProgrammeBookingItem[] = club && Array.isArray(club.items) ? club.items : [];

  const clubConfirmedItems = clubItems
    .filter(it => isConfirmedClubItem(it))
    .slice()
    .sort(sortBookingItemsForLists);

  const clubPreselectedItems = clubItems
    .filter(it => isSelectedClubItem(it))
    .slice()
    .sort(sortBookingItemsForLists);

  const clubWaitingItems = clubItems
    .filter(it => isConfirmedWaitingItem(it))
    .slice()
    .sort(sortBookingItemsForLists);

  const clubPreselectedWaitingItems = clubItems
    .filter(it => isSelectedWaitingItem(it))
    .slice()
    .sort(sortBookingItemsForLists);

  const clubPendingRemoveItems = clubItems
    .filter(it => !!it.uiState?.isPendingRemove)
    .slice()
    .sort(sortBookingItemsForLists);

  const liveClubMaxParticipants =
    club && typeof club.maxParticipants === 'number' ? Number(club.maxParticipants) : null;

  const liveClubFilledCount = club && typeof club.filledCount !== 'undefined' ? Number(club.filledCount || 0) : 0;

  const liveClubRemainingPlaces =
    club && typeof club.remainingPlaces === 'number' ? Number(club.remainingPlaces) : null;

  return (
    <SimpleModal
      isOpen={true}
      title={`Club lists: ${clubName}`}
      showFooter={false}
      onCloseClick={onClose}
      customClass="pbBookingModal pbBookingListsModal"
      dialogStyle={{ maxWidth: 980 }}
    >
      <div className="p-2">
        {!club ? (
          <div className="alert alert-light border mb-0">Club data is no longer available for this day.</div>
        ) : (
          <>
            <div className="cpClubListsHero">
              <div className="cpClubListsHeroMain">
                <div className="cpClubListsHeroTitle">{clubName}</div>
                <div className="cpClubListsHeroSubtitle">
                  Review confirmed places, pending club selections, waiting list and pending WL.
                </div>
              </div>

              <div className="cpClubListsHeroStats">
                <div className="cpClubListsHeroStat">
                  <div className="cpClubListsHeroStatLabel">Places</div>
                  <div className="cpClubListsHeroStatValue">
                    {liveClubMaxParticipants === null ? 'Unlimited' : liveClubMaxParticipants}
                  </div>
                </div>

                <div className="cpClubListsHeroStat">
                  <div className="cpClubListsHeroStatLabel">Filled</div>
                  <div className="cpClubListsHeroStatValue">{liveClubFilledCount}</div>
                </div>

                <div className="cpClubListsHeroStat">
                  <div className="cpClubListsHeroStatLabel">Remaining</div>
                  <div className="cpClubListsHeroStatValue">
                    {liveClubRemainingPlaces === null ? 'Unlimited' : liveClubRemainingPlaces}
                  </div>
                </div>
              </div>
            </div>

            <div className="cpClubListsKpis">
              <div className="cpClubListsKpi cpClubListsKpi--club">
                <span className="cpClubListsKpiLabel">In club</span>
                <span className="cpClubListsKpiValue">{clubConfirmedItems.length}</span>
              </div>

              <div className="cpClubListsKpi cpClubListsKpi--selectedClub">
                <span className="cpClubListsKpiLabel">Pre-selected club</span>
                <span className="cpClubListsKpiValue">{clubPreselectedItems.length}</span>
              </div>

              <div className="cpClubListsKpi cpClubListsKpi--waiting">
                <span className="cpClubListsKpiLabel">In waiting list</span>
                <span className="cpClubListsKpiValue">{clubWaitingItems.length}</span>
              </div>

              <div className="cpClubListsKpi cpClubListsKpi--selectedWaiting">
                <span className="cpClubListsKpiLabel">Pre-selected WL</span>
                <span className="cpClubListsKpiValue">{clubPreselectedWaitingItems.length}</span>
              </div>
            </div>

            <div className="cpClubListsGrid">
              <div className="cpClubListBlock">
                <div className="cpClubListTitle">
                  <span>In club</span>
                  <span className="badge badge-success">{clubConfirmedItems.length}</span>
                </div>

                {!clubConfirmedItems.length ? (
                  <div className="cpClubListEmpty">Nobody currently confirmed in the club.</div>
                ) : (
                  <div className="cpClubListItems">
                    {clubConfirmedItems.map(it => (
                      <div key={String(it.messageId)} className="cpClubListItem cpClubListItem--club">
                        <div className="cpClubListName">{String(propz.get(it, ['student', 'name'], 'Student'))}</div>
                        <div className="cpClubListMeta">
                          {typeof it.priority === 'number' ? <span>P{it.priority}</span> : null}
                          {it.acceptedAt ? <span>{fmtAcceptedAt(it.acceptedAt)}</span> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="cpClubListBlock">
                <div className="cpClubListTitle">
                  <span>Pre-selected club</span>
                  <span className="badge badge-info">{clubPreselectedItems.length}</span>
                </div>

                {!clubPreselectedItems.length ? (
                  <div className="cpClubListEmpty">No pending club selections.</div>
                ) : (
                  <div className="cpClubListItems">
                    {clubPreselectedItems.map(it => (
                      <div key={String(it.messageId)} className="cpClubListItem cpClubListItem--selectedClub">
                        <div className="cpClubListName">{String(propz.get(it, ['student', 'name'], 'Student'))}</div>
                        <div className="cpClubListMeta">
                          {typeof it.priority === 'number' ? <span>P{it.priority}</span> : null}
                          {it.acceptedAt ? <span>{fmtAcceptedAt(it.acceptedAt)}</span> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="cpClubListBlock">
                <div className="cpClubListTitle">
                  <span>Waiting list</span>
                  <span className="badge badge-warning">{clubWaitingItems.length}</span>
                </div>

                {!clubWaitingItems.length ? (
                  <div className="cpClubListEmpty">Nobody currently in the waiting list.</div>
                ) : (
                  <div className="cpClubListItems">
                    {clubWaitingItems.map(it => (
                      <div key={String(it.messageId)} className="cpClubListItem cpClubListItem--waiting">
                        <div className="cpClubListName">{String(propz.get(it, ['student', 'name'], 'Student'))}</div>
                        <div className="cpClubListMeta">
                          {typeof it.priority === 'number' ? <span>P{it.priority}</span> : null}
                          {it.acceptedAt ? <span>{fmtAcceptedAt(it.acceptedAt)}</span> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="cpClubListBlock">
                <div className="cpClubListTitle">
                  <span>Pre-selected WL</span>
                  <span className="badge badge-secondary">{clubPreselectedWaitingItems.length}</span>
                </div>

                {!clubPreselectedWaitingItems.length ? (
                  <div className="cpClubListEmpty">No pending waiting list selections.</div>
                ) : (
                  <div className="cpClubListItems">
                    {clubPreselectedWaitingItems.map(it => (
                      <div key={String(it.messageId)} className="cpClubListItem cpClubListItem--selectedWaiting">
                        <div className="cpClubListName">{String(propz.get(it, ['student', 'name'], 'Student'))}</div>
                        <div className="cpClubListMeta">
                          {typeof it.priority === 'number' ? <span>P{it.priority}</span> : null}
                          {it.acceptedAt ? <span>{fmtAcceptedAt(it.acceptedAt)}</span> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {clubPreselectedItems.length > 0 ||
            clubPreselectedWaitingItems.length > 0 ||
            clubPendingRemoveItems.length > 0 ? (
              <div className="cpClubListsFooterActions">
                <button
                  type="button"
                  className="btn pbBookingModalPrimary"
                  disabled={actionKey !== null}
                  onClick={() => onConfirmClub(clubId)}
                >
                  {actionKey === `CONFIRM_CLUB:${String(clubId)}` ? 'Saving...' : 'Confirm club allocations'}
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </SimpleModal>
  );
};

ClubProgrammeClubListsModal.displayName = 'ClubProgrammeClubListsModal';
